import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import Menu from "@components/Menu";
import UserContext from '@components/context/userContext';
import sequencesService from '@services/sequences';
import tpvsService from '@services/tpvs';
import atService from '@services/at';
import apolloService from '@services/apollo';
import Input from "@components/Input";
import AtLogin from "@components/AtLogin";

import {
  MdReceipt,
  MdTextSnippet,
  MdCreditCard,
  MdCheckBox,
  MdCalculate,
  MdLocalShipping,
  MdWarning,
  MdAdd,
} from "react-icons/md";

import "./type.css";
import TestEnvironmentAlert from "@components/TestEnvironmentAlert";

// Mapeamento de tipos de documentos para as suas séries
const seriesMapping = {
  'facturas': 'FT_epos',
  'vendas_dinheiro': 'FR_epos',
  'recibos': 'RG_epos',
  'notas_credito': 'NC_epos',
  'orcamento': 'OR_epos',
  'guias_transporte': 'GT_epos',
};

// Função para obter mapeamento AT
function getATMapDocType(doctype) {
  switch (doctype) {
    case "FT":
    case "FT_epos":
      return {
        tipo: "FT",
        classe: "SI",
      };
    case "FR":
    case "FR_epos":
      return {
        tipo: "FR",
        classe: "SI",
      };
    case "NC":
    case "NC_epos":
      return {
        tipo: "NC",
        classe: "SI",
      };
    case "RG":
    case "RG_epos":
      return {
        tipo: "RG",
        classe: "PY",
      };
    case "GT":
    case "GT_epos":
      return {
        tipo: "GT",
        classe: "MG",
      };
    default:
      return {
        tipo: "FT",
        classe: "SI",
      };
  }
}

const zeroPad = (num, places) => String(num).padStart(places, "0");

function categorize(string){
  if (string.includes('EPOSFT')){
    return 'EPOSFT';
  }
  if (string.includes('EPOSFR')){
    return 'EPOSFR';
  }
  if (string.includes('EPOSRG')){
    return 'EPOSRG';
  }
  if (string.includes('EPOSNC')){
    return 'EPOSNC';
  }
  if (string.includes('EPOSGT')){
    return 'EPOSGT';
  }
  if (string.includes('FT')){
    return 'FT';
  }
  if (string.includes('NC')){
    return 'NC';
  }
  if (string.includes('FR')){
    return 'FR';
  }
  if (string.includes('RG')){
    return 'RG';
  }
  if (string.includes('GT')){
    return 'GT';
  }
  if (string.includes('F')){
    return 'F';
  }
}

const documentTypes = [
  {
    id: "facturas",
    title: "Fatura",
    description: "Emitir uma nova fatura para cliente",
    icon: <MdReceipt />,
    color: "bg-primary",
    bgColor: "#337ab7",
  },
  {
    id: "vendas_dinheiro",
    title: "Fatura/Recibo",
    description: "Emitir fatura com recibo integrado",
    icon: <MdCheckBox />,
    color: "bg-info",
    bgColor: "#5bc0de",
  },
  {
    id: "recibos",
    title: "Recibo",
    description: "Criar um recibo de pagamento",
    icon: <MdCreditCard />,
    color: "bg-success",
    bgColor: "#5cb85c",
  },
  {
    id: "notas_credito",
    title: "Nota de Crédito",
    description: "Emitir nota de crédito para devolução",
    icon: <MdTextSnippet />,
    color: "bg-danger",
    bgColor: "#d9534f",
  },
  {
    id: "orcamento",
    title: "Orçamento",
    description: "Preparar um novo orçamento",
    icon: <MdCalculate />,
    color: "bg-warning",
    bgColor: "#f0ad4e",
    comingSoon: true,
  },
  {
    id: "guias_transporte",
    title: "Guia de Transporte",
    description: "Emitir guia de transporte para envio",
    icon: <MdLocalShipping />,
    color: "bg-default",
    bgColor: "#777777",
  },
];

function App() {
  const profile = useContext(UserContext);
  const history = useHistory();
  const [seriesStatus, setSeriesStatus] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [seriesName, setSeriesName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [atLogin, setAtLogin] = useState(null);
  const [isAtLoginValid, setIsAtLoginValid] = useState(false);

  useEffect(() => {
    const checkSeries = async () => {
      const status = {};
      
      // Verificar séries para cada tipo de documento
      for (const docType of documentTypes) {
        if (docType.comingSoon) {
          status[docType.id] = { hasSeries: true }; // Ignorar "coming soon"
          continue;
        }
        
        const seriesKey = seriesMapping[docType.id];
        if (seriesKey) {
          try {
            const response = await sequencesService.get(profile.idEmpresa, seriesKey);
            status[docType.id] = {
              hasSeries: response && response.length > 0,
              series: response && response.length > 0 ? response[0].sequence : null
            };
          } catch (error) {
            console.error(`Erro ao verificar série para ${docType.id}:`, error);
            status[docType.id] = { hasSeries: false };
          }
        }
      }
      
      setSeriesStatus(status);
    };

    if (profile.idEmpresa) {
      checkSeries();
    }
  }, [profile.idEmpresa]);

  const handleSelectDocument = (docType) => {
    if (docType.comingSoon) return;
    
    // Verificar se tem série antes de redirecionar
    if (seriesStatus[docType.id] && !seriesStatus[docType.id].hasSeries) {
      // Mostrar modal para criar série
      setSelectedDocType(docType);
      setSeriesName(`EPOS${seriesMapping[docType.id].replace('_epos', '')}01`);
      setShowCreateModal(true);
      return;
    }
    
    history.push("/sales/new/" + docType.id);
  };

  const handleCreateSeries = async () => {
    if (!seriesName.trim()) {
      NotificationManager.error('Por favor, introduza um nome para a série', 'Erro', 3000);
      return;
    }

    setIsCreating(true);
    try {
      const seriesKey = seriesMapping[selectedDocType.id];
      const { tipo, classe } = getATMapDocType(seriesKey);
      
      // Buscar TPVs para o tipo de documento
      const tpvs = await tpvsService.getBulk(profile.idEmpresa, 'production');
      const filteredTpvs = tpvs.filter(tpv => 
        tpv.doctype === seriesKey && tpv.posto === ''
      );

      const startDate = new Date().toISOString().substr(0, 10);
      const finalSerieName = seriesName || `EPOS${seriesKey.replace('_epos', '')}01`;
      
      const seriesData = {
        idempresa: profile.idEmpresa,
        environment: 'production',
        posto: '',
        tpv: '',
        serie: finalSerieName,
        numInicialSeq: 1,
        startAt: startDate,
        doctype: seriesKey,
        data: {
          serie: finalSerieName,
          tipoSerie: "N",
          classeDoc: classe,
          tipoDoc: tipo,
          numInicialSeq: 1,
          dataInicioPrevUtiliz: startDate,
          numCertSWFatur: 942,
          meioProcessamento: "PI",
        },
      };

      const atResponse = await atService.registerSeries(seriesData);
      
      console.log('Resposta completa da AT:', JSON.stringify(atResponse, null, 2));
      console.log('success:', atResponse?.success);
      console.log('data:', atResponse?.data);
      
      // Status codes de sucesso da AT: 2001 (sucesso), 4001 (já existe mas é ok)
      // Nota: A série já foi criada na BD pelo endpoint registarSerie.php
      if (atResponse && atResponse.success === true && atResponse.data) {
        // Sincronizar com Apollo se houver TPVs
        if (filteredTpvs.length > 0) {
          const tpv = filteredTpvs[0];
          await apolloService.syncSeries({
            idempresa: profile.idEmpresa,
            series: [{
              ...tpv,
              serie: finalSerieName,
              atcud: atResponse.data,
              date: startDate,
              doctype: seriesKey,
              tipo,
              classe,
            }]
          });
        }

        NotificationManager.success('Série criada e registada na AT com sucesso!', 'Sucesso', 3000);
        
        setSeriesStatus(prev => ({
          ...prev,
          [selectedDocType.id]: {
            hasSeries: true,
            series: finalSerieName
          }
        }));

        setShowCreateModal(false);
        history.push("/sales/new/" + selectedDocType.id);
      } else {
        console.error('Resposta AT:', atResponse);
        throw new Error('Erro ao registar série na AT');
      }
    } catch (error) {
      console.error('Erro ao criar série:', error);
      console.error('Detalhes do erro:', error?.response?.data || error?.message);
      const errorMessage = error?.response?.data?.error || error?.message || 'Erro ao criar série. Por favor, tente novamente.';
      NotificationManager.error(errorMessage, 'Erro', 5000);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setSelectedDocType(null);
    setSeriesName('');
    setAtLogin(null);
    setIsAtLoginValid(false);
  };

  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: "100%" }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: "left",
              marginTop: "75px",
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li>
              <a href="/sales">Faturação</a>
            </li>
            <li className="active">
              <span>Novo Documento</span>
            </li>
          </ol>
        </div>
      </div>
      <TestEnvironmentAlert />
      <div className="fullContainer sales-type">
        <div className="row">
          <div className="col-xs-12 text-center">
            <h1 className="page-header">Documentos de Faturação</h1>
            <p className="lead">
              Selecione o tipo de documento fiscal que deseja criar
            </p>
          </div>
        </div>

        <div className="row">
          {documentTypes.map((docType) => {
            const hasSeries = seriesStatus[docType.id]?.hasSeries ?? true;
            const isDisabled = docType.comingSoon || !hasSeries;
            
            return (
              <div key={docType.id} className="col-sm-6 col-md-4">
                <div
                  className={`panel panel-default document-card ${
                    isDisabled ? "disabled" : ""
                  }`}
                  onClick={() => !hasSeries || docType.comingSoon ? null : handleSelectDocument(docType)}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: docType.comingSoon ? 0.5 : (isDisabled ? 0.7 : 1),
                    pointerEvents: docType.comingSoon ? 'none' : 'auto'
                  }}
                >
                  <div className="panel-body" style={{ pointerEvents: 'auto' }}>
                    <div className="media">
                      <div className="media-left">
                        <div
                          className="icon-container"
                          style={{ backgroundColor: docType.bgColor }}
                        >
                          {docType.icon}
                        </div>
                      </div>
                      <div className="media-body">
                        <h4 className="media-heading">
                          {docType.title}
                          {docType.comingSoon && (
                            <small className="text-muted"> (Brevemente)</small>
                          )}
                        </h4>
                        <p>{docType.description}</p>
                        {!hasSeries && !docType.comingSoon && (
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectDocument(docType);
                            }}
                            style={{ 
                              marginTop: '8px', 
                              padding: '8px',
                              backgroundColor: '#d1ecf1',
                              border: '1px solid #0c5460',
                              borderRadius: '4px',
                              color: '#0c5460',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <MdWarning size={16} />
                            <span>Clique para configurar a série</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal para criar série */}
      {showCreateModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={handleCancelCreate}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Criar Série</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <p>
                Para criar documentos do tipo <strong>{selectedDocType?.title}</strong>, 
                é necessário configurar uma série primeiro.
              </p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                A série será registada na Autoridade Tributária (AT).
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Login AT *
              </label>
              <AtLogin
                onLogin={(loginData) => {
                  setAtLogin(loginData);
                  setIsAtLoginValid(!!loginData);
                }}
                selectedEmpresas={false}
              />
              {!isAtLoginValid && (
                <p style={{ fontSize: '12px', color: '#d9534f', marginTop: '8px' }}>
                  É necessário validar o login AT para criar séries
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Nome da Série
              </label>
              <Input
                inputType="text"
                name="seriesName"
                value={seriesName}
                handleChange={(e) => setSeriesName(e.target.value)}
                placeholder="Ex: EPOSFT01, EPOSRG01, etc."
                disabled={isCreating}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-default"
                onClick={handleCancelCreate}
                disabled={isCreating}
                style={{ minWidth: '100px' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateSeries}
                disabled={isCreating || !seriesName.trim() || !isAtLoginValid}
                style={{ minWidth: '100px', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}
              >
                {isCreating ? (
                  <>
                    <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                    A criar...
                  </>
                ) : (
                  <>
                    <MdAdd size={16} />
                    Criar Série
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
